import { useWalletInfo, useWeb3ModalAccount } from '@web3modal/ethers/vue';
import { Pusher__factory } from 'public/abi/evm/types';
import useProvider from '~/composables/useProvider';
import type { ContractCallParams } from '~/types/Web3Provider';
import type { TransactionResponse } from 'ethers';
import { useMainStore } from '~/stores/mainStore';
import { getCancelErrorMessage } from '~/utils';

export default async () => {
  const { address } = useWeb3ModalAccount();
  const { sendContractMethod } = useSendContractMethod();
  const { pusherContract } = useEnvs();
  const { getProvider } = useProvider();
  const { t } = useI18n();
  const { $errorHandler } = useNuxtApp();
  const { walletInfo } = useWalletInfo();
  const store = useMainStore();
  const { getSigner } = useProvider();

  const isLoading = ref(true);
  const isGameStarted = ref(false);

  const sendContractMethodExtended = async <T extends Record<any, any>, M extends keyof T>(
    sendParams: ContractCallParams<T, M>,
    onConfirmation?: (confirmations?: number) => void
  ) => {
    const from = address.value;
    return sendContractMethod({ ...sendParams, from }, onConfirmation);
  };

  const pusherPlay = async (tokenAddress: string, betsCount: number) => {
    return sendContractMethodExtended({
      contract: Pusher__factory.connect(pusherContract, await getSigner()),
      methodName: 'play',
      args: [tokenAddress, betsCount]
    });
  };

  const getLastAddedSPTimeAndMaxSP = (tokenAddress: string) => {
    return Pusher__factory.connect(pusherContract, getProvider()).getLastAddedSPTimeAndMaxSP(
      String(address.value),
      tokenAddress
    );
  };

  async function play(betsCount: number, tokenAddress: string): Promise<string | undefined> {
    try {
      if (!address.value) {
        return;
      }

      isLoading.value = true;

      const transaction: TransactionResponse | undefined = await pusherPlay(tokenAddress, betsCount);

      if (!transaction?.hash) {
        return;
      }

      saveHashToLocalStorage(t('coinPusherGameStarted'), transaction.hash);
      store.updateVersion();

      await transaction.wait();

      modifyHistoryHashStatus(transaction.hash, 'Done');
      store.updateVersion();

      isGameStarted.value = true;

      return transaction.hash;
    } catch (error: unknown) {
      const currentProviderKind = walletInfo.value?.name;

      if (currentProviderKind) {
        const errorMessage = getCancelErrorMessage(error, currentProviderKind);
        $errorHandler(t(errorMessage));
      }

      console.log(error);
    } finally {
      isLoading.value = false;
    }
  }

  return {
    isLoading,
    isGameStarted,
    getLastAddedSPTimeAndMaxSP,
    play
  };
};
